import { useEffect, useState } from 'react';
import { Button, Form, Select } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import type { AdminLocation, AdminLocationSummary } from '@goelectric/shared-types';
import { useAuth } from '@/features/auth/context/AuthContext';
import { NewLocationDrawer } from '@/features/locaciones/components/NewLocationDrawer';
import { useLocations } from '@/features/locaciones/hooks/useLocations';
import { canWriteLocaciones } from '@/layouts/navigation';
import { LOCATION_OPTIONS_LIMIT } from '../utils/stationForm';

/** Debounce de la búsqueda antes de golpear el backend (server-side). */
const SEARCH_DEBOUNCE_MS = 300;

export interface StationLocationOption {
  id: string;
  name: string;
}

interface StationLocationFieldsProps {
  /**
   * Notifica la Ubicación elegida (o `null` al limpiar). El host lo usa para mostrar el
   * nombre en la Revisión sin un segundo request. Cualquier ubicación es válida, tenga o
   * no aliado.
   */
  onSelect?: (location: StationLocationOption | null) => void;
  /**
   * Opción fija que se antepone a las de la búsqueda. En edición se pasa la ubicación
   * actual para que el `Select` la muestre aunque no venga en el primer lote (o esté
   * `INACTIVE`, que la búsqueda filtra).
   */
  pinnedOption?: StationLocationOption | null;
}

/**
 * Paso 2 del alta de Cargador: la Ubicación donde está instalado el equipo. Búsqueda
 * server-side (`q` + debounce) sobre `useLocations`; el `locationId` es un campo requerido.
 *
 * La ubicación NO se filtra por aliado: la Fase 1 habilitó asociar cargadores a cualquier
 * ubicación, con o sin aliado.
 *
 * TG-125: el desplegable ofrece "Nueva ubicación", que abre `NewLocationDrawer` (reuso del
 * `LocationForm`). Al crear (y sincronizar) con éxito, se auto-selecciona acá mismo: el
 * `pinnedOption` interno la muestra aunque no venga en el lote de búsqueda, y `onSelect`
 * avisa al host. Es self-contained, así que sirve igual en alta (`CreateStationPage`) y en
 * edición (`StationFormModal`).
 */
export function StationLocationFields({ onSelect, pinnedOption }: StationLocationFieldsProps) {
  const [query, setQuery] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Ubicación creada desde el aside: se antepone al lote hasta que la búsqueda la traiga.
  const [createdOption, setCreatedOption] = useState<StationLocationOption | null>(null);
  const form = Form.useFormInstance();
  const { user } = useAuth();
  const canCreate = canWriteLocaciones(user?.role);
  const locations = useLocations({
    limit: LOCATION_OPTIONS_LIMIT,
    initialFilters: { status: 'ACTIVE' },
  });
  const { setFilters } = locations;

  // Sincroniza la búsqueda con el backend con debounce (evita una request por tecla).
  useEffect(() => {
    const handle = window.setTimeout(() => {
      const next = query.trim();
      if ((locations.filters.q ?? '') !== next) {
        setFilters({ status: 'ACTIVE', q: next || undefined });
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [query, locations.filters.q, setFilters]);

  const options = locations.data.map((location: AdminLocationSummary) => ({
    value: location.id,
    label: location.name,
  }));
  const nameById = new Map(locations.data.map((location) => [location.id, location.name]));

  // Antepone las ubicaciones fijas (creada en el aside o la actual en edición) si no
  // vinieron ya en el lote de la búsqueda.
  const extraOptions: { value: string; label: string }[] = [];
  for (const extra of [createdOption, pinnedOption]) {
    if (extra && !nameById.has(extra.id)) {
      nameById.set(extra.id, extra.name);
      extraOptions.push({ value: extra.id, label: extra.name });
    }
  }
  const selectOptions = [...extraOptions, ...options];

  /** Creación exitosa en el aside: selecciona la ubicación y avisa al host. */
  const handleCreated = (location: AdminLocation) => {
    const option = { id: location.id, name: location.name };
    setCreatedOption(option);
    form.setFieldValue('locationId', location.id);
    onSelect?.(option);
    setDrawerOpen(false);
  };

  return (
    <>
      <Form.Item
        label="Ubicación"
        name="locationId"
        rules={[{ required: true, message: 'Selecciona la ubicación.' }]}
        extra={
          canCreate ? (
            <Button
              type="link"
              size="small"
              icon={<PlusOutlined />}
              htmlType="button"
              onClick={() => setDrawerOpen(true)}
            >
              ¿No la encuentras? Crea una nueva
            </Button>
          ) : undefined
        }
      >
        <Select
          showSearch
          filterOption={false}
          loading={locations.loading}
          placeholder="Busca y selecciona la ubicación"
          options={selectOptions}
          onSearch={setQuery}
          onChange={(value: string | undefined) =>
            onSelect?.(value ? { id: value, name: nameById.get(value) ?? value } : null)
          }
          notFoundContent={locations.loading ? undefined : 'Sin ubicaciones para la búsqueda'}
        />
      </Form.Item>

      <NewLocationDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onCreated={handleCreated}
      />
    </>
  );
}
